import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { TableCell, TableHead, TableRow, Typography } from '@mui/material';
import clsx from 'clsx';
import { GANTT_CHART_CONTAINER } from 'constants/ids';

import {
  GANTT_CHART_MONTH_QUANTITY,
  GANTT_FIRST_COLUMN_WIDTH,
  GANTT_ONE_SIDE_PADDING,
  GANTT_SMALL_MONTH_WIDTH,
} from 'components/OnePageTemplates/common/GanttChart/constants/ganttChart';
import { STYLE_VARIANTS } from 'components/OnePageTemplates/interfaces/variants';

import { IGanttChartHeaderProps as IProps } from './types';
import { getGanttHeaderLabels } from '../../helpers';

import styles from './GanttChartHeader.module.scss';

export const GanttChartHeader: FC<IProps> = ({ startDate, endDate, setMonthWidth, monthWidth, variant }) => {
  const displayData = getGanttHeaderLabels(startDate, endDate);
  const isOutlined = variant === STYLE_VARIANTS.OUTLINED;

  const { t } = useTranslation();

  useEffect(() => {
    const chartWrapper = document.querySelector(`#${GANTT_CHART_CONTAINER}`) as HTMLElement;

    if (displayData.length < GANTT_CHART_MONTH_QUANTITY && chartWrapper) {
      setMonthWidth(
        (chartWrapper.offsetWidth - GANTT_FIRST_COLUMN_WIDTH - GANTT_ONE_SIDE_PADDING * 2) / displayData.length,
      );

      return;
    }

    if (displayData.length > GANTT_CHART_MONTH_QUANTITY) {
      setMonthWidth(GANTT_SMALL_MONTH_WIDTH);
    }
  }, [displayData.length, setMonthWidth]);

  return (
    <TableHead>
      <TableRow>
        <TableCell
          className={clsx(styles.stageWrapper, {
            [styles.outlined]: isOutlined,
          })}
          sx={{
            width: `${GANTT_FIRST_COLUMN_WIDTH}px`,
            minWidth: `${GANTT_FIRST_COLUMN_WIDTH}px`,
          }}
        >
          <Typography
            component="p"
            className={clsx(styles.stagesLabel, {
              [styles.outlined]: isOutlined,
            })}
            variant="subtitle1"
          >
            {!isOutlined && t('onePageMapping.stages')}
          </Typography>
        </TableCell>
        <TableCell
          className={clsx(styles.monthsWrapper, {
            [styles.outlined]: isOutlined,
          })}
        >
          {displayData.map((item, index) => (
            <Typography
              className={styles.monthLabels}
              variant="h6"
              sx={{ width: `${monthWidth}px` }}
              key={index + item}
            >
              {item}
            </Typography>
          ))}
        </TableCell>
      </TableRow>
    </TableHead>
  );
};
